import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {applyRedactFont} from "../../helpers/redact";

function toNumber(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'bigint') return Number(value);
    if (value && typeof value === 'object' && typeof value.toNumber === 'function') {
        return value.toNumber();
    }
    if (typeof value === 'number') return value;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function sumSeries(series) {
    let total = 0;
    (series || []).forEach((sample) => {
        total += toNumber(sample) ?? 0;
    });
    return total;
}

function throughputFromRecord(record) {
    const body = record?.body ?? record ?? {};
    return sumSeries(body.download_bytes_median);
}

export class ShaperSiteContextSankey extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.navContext = null;
        this.siteContext = null;
        this.siteFunnel = null;
    }

    title() {
        return "Context";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Site Contexst</h5><p>Shows the path to the site and exits, highlighting traffic and retransmits</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        let gd = this.graphDiv();
        gd.style.height = "500px";
        base.appendChild(gd);
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperSiteContextSankeyGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "SitePage", "SiteFunnel" ];
    }

    onMessage(msg) {
        if (msg.Data.target === "shaper_navigation") {
            if (msg.Data.is_flat) {
                this.navContext = null;
            } else {
                const blob = (msg.Data.blob_raw ?? msg.Data.blob) || [];
                this.navContext = blob[0] || null;
            }
        }
        if (msg.Data.target === "site_bundle") {
            this.siteContext = msg.Data.blob_raw ?? msg.Data.blob;
        }
        if (msg.Data.target === "site_funnel") {
            this.siteFunnel = msg.Data.blob_raw ?? msg.Data.blob;
        }
        if (this.navContext === null || this.siteContext === null || this.siteFunnel === null) {
            // Still waiting for data
            return;
        }

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }

        // We have all the data we need, let's make the graph
        let nodes = [];
        let links = [];
        let nodeNames = new Set(); // Track unique node names
        const siteBundle = this.siteContext?.[0] ?? {};
        const circuitBundle = this.siteContext?.[1] ?? {};
        const bundleSiteThroughput = new Map();
        const bundleCircuitThroughput = new Map();
        Object.entries(siteBundle).forEach(([hash, record]) => {
            bundleSiteThroughput.set(String(hash), throughputFromRecord(record));
        });
        Object.entries(circuitBundle).forEach(([hash, record]) => {
            bundleCircuitThroughput.set(String(hash), throughputFromRecord(record));
        });
        const funnelSites = this.siteFunnel?.[1] ?? {};
        let lastLayer = null;
        this.navContext.forEach((nav) => {
            for (const [key, value] of Object.entries(nav)) {
                switch (key) {
                    case "ShaperRoot": {
                        if (!nodeNames.has(value.name)) {
                            nodeNames.add(value.name);
                            nodes.push({
                                name: value.name,
                                link: "shaper.html?shaper_id=" + value.shaper_id + "&nodeId=" + window.nodeId,
                            });
                        }
                        lastLayer = value.name;
                    } break;
                    case "Layer" : {
                        if (!nodeNames.has(value.name)) {
                            nodeNames.add(value.name);
                            nodes.push({
                                name: value.name,
                                link: "shaper_site.html?site=" + value.hash + "&nodeId=" + window.nodeId,
                            });
                        }
                        let rootValue = 0;
                        for (const [siteHash, siteRecord] of Object.entries(funnelSites)) {
                            const body = siteRecord?.body ?? siteRecord ?? {};
                            const candidateHash = body.site_hash ?? siteHash;
                            if (String(candidateHash) === String(value.hash)) {
                                rootValue += sumSeries(body.download_bytes_median);
                            }
                        }
                        const linkValue = rootValue > 0 ? rootValue : 1;
                        links.push({
                            source: lastLayer,
                            target: value.name,
                            value: linkValue,
                        });
                        lastLayer = value.name;
                    } break;
                    case "Destinations": {
                        value.destinations.forEach((dest) => {
                            for (const [dkey, dvalue] of Object.entries(dest)) {
                                switch (dkey) {
                                    case "Site": {
                                        const val = bundleSiteThroughput.get(String(dvalue.hash));
                                        const found = val !== undefined;
                                        if (found && !nodeNames.has(dvalue.name)) {
                                            nodeNames.add(dvalue.name);
                                            nodes.push({
                                                name: dvalue.name,
                                                link: "shaper_site.html?site=" + dvalue.hash + "&nodeId=" + window.nodeId,
                                            });
                                            links.push({
                                                source: lastLayer,
                                                target: dvalue.name,
                                                value: Math.max(toNumber(val) ?? 0, 1),
                                            });
                                        }
                                    } break;
                                    case "Circuit": {
                                        const val = bundleCircuitThroughput.get(String(dvalue.hash));
                                        const found = val !== undefined;
                                        if (found && !nodeNames.has(dvalue.name)) {
                                            nodeNames.add(dvalue.name);
                                            nodes.push({
                                                name: dvalue.name,
                                                link: "shaper_circuit.html?circuit=" + dvalue.hash + "&nodeId=" + window.nodeId,
                                            });
                                            links.push({
                                                source: lastLayer,
                                                target: dvalue.name,
                                                value: Math.max(toNumber(val) ?? 0, 1),
                                            });
                                        }
                                    } break;
                                    default: {}
                                }
                            }
                        });
                    } break;
                    default: {}
                }
            }
        });
        let data = {
            nodes: nodes,
            links: links,
        };

        // Update the graph with the data
        if (this.graph) {
            this.lastGraphState = data;
            this.graph.update(data);
        }
    }

}

class ShaperSiteContextSankeyGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        let labelConfig = {
            formatter: function(params) {
                return params.name.length > 15 ? params.name.substring(0, 15) + '...' : params.name;
            }
        };
        applyRedactFont(labelConfig);
        this.option = {
            series: [
            {
                type: 'sankey',
                data: [],
                links: [],
                nodeGap: 20,
                label: labelConfig
            }
        ]};
        this.option && this.chart.setOption(this.option);
        
        // Add click event handler for nodes and labels
        this.chart.on('click', 'series.sankey.node', (params) => {
            if (params.data && params.data.link) {
                window.location.href = params.data.link;
            }
        });
        
        this.chart.on('click', 'series.sankey.label', (params) => {
            if (params.data && params.data.link) {
                window.location.href = params.data.link;
            }
        });
    }

    update(data) {
        this.chart.hideLoading();

        this.option.series[0].data = data.nodes;
        this.option.series[0].links = data.links;

        // Apply
        this.chart.setOption(this.option);
    }
}
